import React from 'react';
import { Heart, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFavorites } from '@/hooks/useFavorites';
import { useAuth } from '@/hooks/useAuth';
import { LazyImage } from '@/components/LazyImage';

interface AirbnbCardProps {
  id: string;
  image?: string;
  title: string;
  subtitle: string;
  distance?: string;
  rating?: number;
  onCardClick: () => void;
  onFavoriteClick: () => void;
  isFavorite?: boolean;
  priority?: boolean;
}

const AirbnbCard: React.FC<AirbnbCardProps> = ({
  id,
  image,
  title,
  subtitle,
  distance,
  rating,
  onCardClick,
  onFavoriteClick,
  isFavorite = false,
  priority = false
}) => {
  const { isAuthenticated } = useAuth();

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      window.location.href = '/auth';
      return;
    }
    
    onFavoriteClick();
  };

  return (
    <div 
      className="flex-shrink-0 w-64 md:w-72 cursor-pointer group"
      onClick={onCardClick}
    >
      {/* Image Container with Overlay Text */}
      <div className="relative h-56 md:h-64">
        {image ? (
          <LazyImage
            src={image}
            alt={title}
            width={288}
            height={256}
            priority={priority}
            className="w-full h-full object-cover rounded-xl"
            placeholder={
              <div className="w-full h-full bg-gray-200 animate-pulse rounded-xl" />
            }
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-300 rounded-xl flex items-center justify-center">
            <span className="text-gray-600 text-3xl font-bold">
              {title.charAt(0)}
            </span>
          </div>
        )}
        
        {/* Overlay gradient for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent rounded-xl" />
        
        {/* City/Country Text Overlay */}
        <div className="absolute top-3 left-3 right-12 z-10">
          <h3 className="font-bold text-white text-base md:text-lg drop-shadow-lg truncate">
            {title}
          </h3>
          <p className="text-white/90 text-sm drop-shadow-lg truncate">{subtitle}</p>
        </div>
        
        {/* Favorite Heart */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleFavoriteClick}
          className={`absolute top-3 right-3 w-7 h-7 p-0 rounded-full backdrop-blur-sm transition-all duration-200 hover:scale-110 z-10 ${
            isFavorite 
              ? 'bg-white/95 text-red-500 hover:bg-white shadow-sm' 
              : 'bg-black/30 text-white hover:bg-black/50'
          }`}
          aria-label={isFavorite ? `Retirer ${title} des favoris` : `Ajouter ${title} aux favoris`}
        >
          <Heart className={`w-3.5 h-3.5 ${isFavorite ? 'fill-current' : ''}`} />
        </Button>
        
        {/* Distance and Rating at bottom if provided */}
        {(distance || rating) && (
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between z-10">
            {distance && (
              <span className="text-xs font-medium text-white drop-shadow-lg">{distance}</span>
            )}
            {rating && (
              <div className="flex items-center gap-1 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                <Star className="w-3 h-3 fill-current text-black" />
                <span className="text-xs font-medium text-black">
                  {rating.toFixed(1)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AirbnbCard;